import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, Suspense, useCallback } from "react";
import { useGLTF } from "@react-three/drei";
import { useModelStore, useUserInterfaceStore, useMusicPlaybackStore } from "../../store";
import { assetsApi } from "../../services/api";

// Placeholder component for loading/error states
function ModelPlaceholder({
  position,
  rotation,
  scale,
  color = "#95a5a6",
  opacity = 0.5,
}) {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <mesh>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color={color} transparent opacity={opacity} />
      </mesh>
    </group>
  );
}

// Error fallback component - renders a 3D placeholder when model fails to load
function ErrorFallback({ position, rotation, scale }) {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <mesh>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#ff6b6b" transparent opacity={0.7} />
      </mesh>
      <mesh position={[0, 0.6, 0]}>
        <sphereGeometry args={[0.2]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
    </group>
  );
}

/**
 * 3D-compatible Error Boundary for GLTF loading
 * Catches errors from useGLTF and renders a 3D fallback mesh instead of crashing
 * Must be a class component since error boundaries require getDerivedStateFromError
 */
class GLTFErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error but don't crash - this is expected for 403s and network issues
    console.warn('GLTF loading failed:', error.message);
  }

  render() {
    if (this.state.hasError) {
      // Render 3D fallback - this is safe inside Canvas
      const { position, rotation, scale } = this.props;
      return <ErrorFallback position={position} rotation={rotation} scale={scale} />;
    }
    return this.props.children;
  }
}


// Component that actually loads the GLTF
function GLTFModel({ modelUrl, model, groupRef }) {
  const { scene } = useGLTF(modelUrl);
  const setModels = useModelStore((s) => s.setModels);
  const playerMode = useUserInterfaceStore((s) => s.playerMode);
  const isEditing = playerMode !== "player";

  return (
    <GLTFModelRenderer
      scene={scene}
      model={model}
      groupRef={groupRef}
      setModels={setModels}
      isEditing={isEditing}
    />
  );
}

// Global scale factor for all 3D assets
const ASSET_SCALE_FACTOR = 0.85;

// Separate component for rendering the loaded GLTF
function GLTFModelRenderer({ scene, model, groupRef, setModels, isEditing }) {
  const { position, rotation, scale, hue, music, musicLoop = true, roughness = 1 } = model;

  // Apply global scale factor to all assets
  const adjustedScale = scale.map(s => s * ASSET_SCALE_FACTOR);

  useEffect(() => {
    if (!scene || !groupRef.current) return;

    try {
      const sourceId = model.id;
      const newModels = [];
      const meshMap = new Map(useModelStore.getState().meshMap);

      groupRef.current.userData = {
        ...groupRef.current.userData,
        isGroupRoot: true,
        sourceId,
      };

      scene.traverse((child) => {
        try {
          if (child.isMesh) {
            child.userData.sourceId = sourceId;
            newModels.push({
              id: child.uuid,
              name: child.name || "Unnamed Mesh",
              ref: { current: child },
            });
            meshMap.set(child.uuid, child);
          }
        } catch (error) {
          console.warn('Error processing mesh:', child.name, error);
          // Continue traversing
        }
      });

      useModelStore.setState((state) => ({
        meshMap,
        models: [...state.models, ...newModels],
      }));

      // Auto-select this model if it was just added (pending selection)
      const pending = useModelStore.getState().pendingSelectionId;
      if (pending && pending === sourceId && groupRef.current) {
        useModelStore.getState().setSelectedMeshes([groupRef.current]);
        useModelStore.getState().setSelectedUUIDs([groupRef.current.uuid]);
        useModelStore.getState().setPendingSelectionId(null);
      }
    } catch (error) {
      console.error('Error setting up GLTF model:', model.id, error);
      // Silently fail - model will render as placeholder/error state
    }
  }, [model.id, scene, setModels, groupRef]);

  return (
    <>
      <group
        ref={groupRef}
        position={position}
        scale={adjustedScale}
        rotation={rotation}
      >
        <HueShiftedClone scene={scene} hue={hue} roughness={roughness} />
      </group>
      {!isEditing && music && (
        <MusicAudioPlayer music={music} modelId={model.id} loop={musicLoop} />
      )}
    </>
  );
}

// Memoize GLBObject to prevent re-renders when other models in the list change
const GLBObject = React.memo(({ model }) => {
  const { url, assetId, position, rotation, scale } = model;
  const [modelUrl, setModelUrl] = useState(url || null);
  const [loadingState, setLoadingState] = useState("loading");
  const [retryCount, setRetryCount] = useState(0);
  const groupRef = useRef();

  // Auto-recover from error state after 30 seconds (e.g. after circuit breaker resets)
  useEffect(() => {
    if (loadingState !== 'error' || !assetId) return;
    const timer = setTimeout(() => {
      setRetryCount(0);
      setLoadingState('loading');
    }, 30000);
    return () => clearTimeout(timer);
  }, [loadingState, assetId]);

  // Error handler for GLTF loading failures
  const handleGLTFError = useCallback((error) => {
    console.error('GLTF loading error for model:', model.id, error);

    // If this is a network-related or circuit breaker error and we haven't retried too much, attempt retry
    const isTemporary = error.message?.includes('fetch') ||
      error.message?.includes('network') ||
      error.message?.includes('CORS') ||
      error.code === 'CIRCUIT_BREAKER_OPEN' ||
      error.name === 'NetworkError';
    if (retryCount < 3 && isTemporary) {
      const jitter = Math.random() * 1000;
      console.log(`Retrying GLTF load (attempt ${retryCount + 1}/4)`);
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
      }, 1000 * (retryCount + 1) + jitter); // Exponential backoff with jitter
    } else {
      setLoadingState("error");
    }
  }, [model.id, retryCount]);

  // Use the handler to prevent unused variable warning
  React.useEffect(() => {
    // This effect ensures handleGLTFError is "used" for potential future error handling
  }, [handleGLTFError]);

  // Track previous assetId/url to know when to reset retry count
  const prevAssetIdRef = useRef(assetId);
  const prevUrlRef = useRef(url);

  // Fetch presigned URL if we have an assetId
  useEffect(() => {
    const loadModelUrl = async () => {
      // Only reset retry count when assetId or url actually changes (not on retry)
      if (prevAssetIdRef.current !== assetId || prevUrlRef.current !== url) {
        setRetryCount(0);
        prevAssetIdRef.current = assetId;
        prevUrlRef.current = url;
      }

      if (!assetId) {
        // No assetId, use provided URL
        if (url && typeof url === "string" && url.trim()) {
          setModelUrl(url);
          setLoadingState("success");
        } else {
          console.error("No valid URL or assetId provided");
          setLoadingState("error");
        }
        return;
      }

      try {
        setLoadingState("loading");

        // Add timeout for API calls
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
        });

        const presignedUrl = await Promise.race([
          assetsApi.getModelUrl(assetId),
          timeoutPromise
        ]);

        if (
          presignedUrl &&
          typeof presignedUrl === "string" &&
          presignedUrl.trim()
        ) {
          setModelUrl(presignedUrl);
          setLoadingState("success");
        } else {
          throw new Error('Invalid presigned URL received');
        }
      } catch (error) {
        console.error("Failed to fetch presigned URL:", error);

        // Only fall back to model.url if it's a permanent URL (not an expired presigned URL)
        const isPresignedUrl = url && url.includes('X-Amz-');
        if (url && typeof url === "string" && url.trim() && !isPresignedUrl) {
          console.log('Falling back to permanent URL');
          setModelUrl(url);
          setLoadingState("success");
        } else {
          // Retry the presigned URL fetch — don't use expired presigned URLs as fallback
          if (retryCount < 3) {
            const jitter = Math.random() * 1000;
            console.log(`Retrying presigned URL fetch (attempt ${retryCount + 1}/4)`);
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
            }, 2000 * (retryCount + 1) + jitter); // Exponential backoff with jitter
            return;
          }

          setLoadingState("error");
        }
      }
    };

    loadModelUrl();
  }, [assetId, url, retryCount]);

  // Handle different loading states
  if (loadingState === "loading" || !modelUrl) {
    return (
      <ModelPlaceholder position={position} rotation={rotation} scale={scale} />
    );
  }

  if (loadingState === "error") {
    return (
      <ErrorFallback position={position} rotation={rotation} scale={scale} />
    );
  }

  return (
    <GLTFErrorBoundary position={position} rotation={rotation} scale={scale}>
      <Suspense
        fallback={
          <ModelPlaceholder
            position={position}
            rotation={rotation}
            scale={scale}
            color="#f39c12"
            opacity={0.6}
          />
        }
      >
        <GLTFModel
          modelUrl={modelUrl}
          model={model}
          groupRef={groupRef}
        />
      </Suspense>
    </GLTFErrorBoundary>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if model actually changed
  // Compare all relevant model properties
  const prev = prevProps.model;
  const next = nextProps.model;

  return (
    prev.id === next.id &&
    prev.url === next.url &&
    prev.assetId === next.assetId &&
    prev.position[0] === next.position[0] &&
    prev.position[1] === next.position[1] &&
    prev.position[2] === next.position[2] &&
    prev.rotation[0] === next.rotation[0] &&
    prev.rotation[1] === next.rotation[1] &&
    prev.rotation[2] === next.rotation[2] &&
    prev.scale[0] === next.scale[0] &&
    prev.scale[1] === next.scale[1] &&
    prev.scale[2] === next.scale[2] &&
    prev.hue === next.hue &&
    prev.roughness === next.roughness &&
    prev.music === next.music &&
    prev.link === next.link &&
    prev.hoverText === next.hoverText
  );
});

export default GLBObject;

function MusicAudioPlayer({ music, modelId, loop = true }) {
  const audioElRef = useRef();
  const isPlaying = useMusicPlaybackStore((s) => s.isPlaying(modelId));
  const registerAudio = useMusicPlaybackStore((s) => s.registerAudio);
  const unregisterAudio = useMusicPlaybackStore((s) => s.unregisterAudio);

  // Create HTML <audio> element and register it so togglePlaying can call play() directly
  // in the user gesture call stack (required by iOS for audio playback)
  useEffect(() => {
    if (!music) return;

    const audioEl = document.createElement('audio');
    audioEl.loop = loop;
    audioEl.src = music;
    document.body.appendChild(audioEl);
    audioElRef.current = audioEl;
    registerAudio(modelId, audioEl);

    // When a non-looping track ends, update store so UI reflects stopped state
    const onEnded = () => {
      useMusicPlaybackStore.getState().setPlaying(modelId, false);
    };
    audioEl.addEventListener('ended', onEnded);

    return () => {
      audioEl.removeEventListener('ended', onEnded);
      audioEl.pause();
      audioEl.removeAttribute('src');
      audioEl.load();
      document.body.removeChild(audioEl);
      audioElRef.current = null;
      unregisterAudio(modelId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [music, modelId, registerAudio, unregisterAudio]);

  // Keep loop attribute in sync with prop
  useEffect(() => {
    if (audioElRef.current) audioElRef.current.loop = loop;
  }, [loop]);

  // Fallback sync for state changes not triggered by togglePlaying (e.g. stopAll)
  useEffect(() => {
    const audioEl = audioElRef.current;
    if (!audioEl) return;

    if (isPlaying) {
      audioEl.play().catch(() => {});
    } else {
      audioEl.pause();
    }
  }, [isPlaying]);

  return null;
}

function HueShiftedClone({ scene, hue, roughness = 1 }) {
  // Clone the scene once per scene change, deep-cloning materials so each
  // instance owns its own material copies (prevents shared-material contamination).
  // We also record the original base HSL of each mesh so hue can be re-applied
  // accurately without floating-point drift from repeated getHSL/setHSL cycles.
  const { clone, baseMaterials } = useMemo(() => {
    const c = scene.clone(true);
    const bases = new Map();
    c.traverse((child) => {
      if (child.isMesh && child.material) {
        try {
          child.material = child.material.clone();
          if (child.material.color && typeof child.material.color.getHSL === 'function') {
            const hsl = {};
            child.material.color.getHSL(hsl);
            bases.set(child.uuid, {
              s: hsl.s === 0 ? 1 : hsl.s,
              l: hsl.l === 0 || hsl.l === 1 ? 0.5 : hsl.l,
            });
          }
        } catch (e) {
          console.warn('Error cloning material:', e);
        }
      }
    });
    return { clone: c, baseMaterials: bases };
  }, [scene]);

  // Apply hue and roughness imperatively via useLayoutEffect, which fires
  // synchronously after React's commit phase but before the THREE.js frame
  // render — so the correct hue is visible on the very first frame (no lag).
  useLayoutEffect(() => {
    clone.traverse((child) => {
      if (child.isMesh && child.material) {
        try {
          if (hue !== undefined) {
            const base = baseMaterials.get(child.uuid);
            if (base && child.material.color) {
              child.material.color.setHSL(hue, base.s, base.l);
            }
          }
          if (typeof roughness === 'number' && roughness >= 0) {
            child.material.roughness = roughness;
          }
          child.material.needsUpdate = true;
        } catch (e) {
          console.warn('Error applying hue/roughness:', e);
        }
      }
    });
  }, [clone, hue, roughness, baseMaterials]);

  // Dispose cloned materials when the scene changes to prevent GPU memory leaks.
  useEffect(() => {
    return () => {
      clone.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.dispose();
        }
      });
    };
  }, [clone]);

  return <primitive object={clone} />;
}
